import { useState, useEffect, useMemo } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Button, Stack, Chip,
  Snackbar, Alert, Backdrop, CircularProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DataGrid } from "@mui/x-data-grid";
import { loadStripe } from "@stripe/stripe-js";
import { getSubscriptionPlans, getUserSubscriptionPlan } from "../api/apiService";


//keep stripePromise outside function component 
const stripePromise = loadStripe("pk_test_yourPublishableKey"); // replace with your real key

export default function Subscription() {
  /* clientId from local storage */
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clientId')) ?? '';
    } catch {
      return '';
    }
  }, []);
  const [billingRows, setBillingRows] = useState([]);
  const [loading, setLoading] = useState(false);
  //const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState("")
  // const [basicPlan, setBasicPlan] = useState([]);
  // const [professionalPlan, setProfessionalPlan] = useState([]);
  // const [enterprisePlan, setEnterprisePlan] = useState([]);
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const fetchBilling = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiUrl}/billing`);
      const data = await res.json();
      setBillingRows(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(error);
      }
      setSnackbar({ open: true, message: "Failed to fetch billing history", severity: "error" });
    }
  };

  const fetchSubscription = async () => {
    try{
      setLoading(true);
      const res = await getSubscriptionPlans();
      //console.log('subscriptions: ',res);
      setPlans(res.data.data);
      // setBasicPlan(res.data.data[0]);
      // setProfessionalPlan(res.data.data[1]);
      // setEnterprisePlan(res.data.data[2]);
      setLoading(false);
    }catch (error){
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching subscription:', error);
      }
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchBilling();
    fetchSubscription();
  }, []);

  useEffect(() => {
    const getCurrentSubscription = async () => {
      try {
        const res = await getUserSubscriptionPlan(clientId);
        setCurrentPlan(res?.data?.data?.plan);
        console.log(res?.data?.data?.plan);
      } catch (error) {
        console.log(error);
      }
    }
    getCurrentSubscription();
  },[clientId])

  const handleSubscribe = async (plan) => {
    if (!plan || !plan.stripePriceId) {
      setSnackbar({
        open: true,
        message: "Invalid plan selection",
        severity: "error",
      });
      return;
    } 
    // else block for testing, to avoid call to pament api
    else if(plan.isActive) {
      setSnackbar({
        open: true,
        message: "Your have selected " + plan.name + " plan, subscription option coming soon.",
        severity: "warning",
      });
      return;
    }

    const priceId = plan.priceId; 

    try {
      //setSubscriptionLoading(true);
      const stripe = await stripePromise;
      const checkoutUrl = process.env.REACT_APP_STRIPE_CHECKOUT_URL || process.env.REACT_APP_API_URL || '';
      
      const response = await fetch(`${checkoutUrl}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId }),
      });

      if (!response.ok) throw new Error("Failed to create checkout session");

      const session = await response.json();

      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) {
        setSnackbar({ open: true, message: result.error.message, severity: "error" });
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      setSnackbar({ open: true, message: "Subscription failed. Try again.", severity: "error" });
    } finally {
      //setSubscriptionLoading(false);
    }
  };

  const billingColumns = [
    { field: 'invoice', headerName: 'Invoice', flex: 1 },
    { field: 'date', headerName: 'Date', flex: 1 },
    { field: 'amount', headerName: 'Amount', flex: 1 },
    {
      field: 'status', headerName: 'Status', flex: 1,
      renderCell: () => <Chip label="Paid" color="success" size="small" />
    },
    {
      field: 'action', headerName: 'Action', flex: 1,
      renderCell: () => <Button size="small">Download</Button>
    },
  ];

  if (loading) {
    return (
      <Box sx={{display:'flex', justifyContent:'center', alignItems:'center', height:'50vh'}}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #ffffff 100%)' }}>
      {/* Spinner */}
      <Backdrop open={loading} sx={{ zIndex: 9999 }}>
        <CircularProgress color="primary" />
      </Backdrop>

      {/* Error/Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" fontWeight={600}>Subscription</Typography>

      <Box sx={{ my: 2 }}>
        {/* Subscription Plans */}
        <Card elevation={0} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ my: 1 }}>Subscription Plans</Typography>
            <Grid container spacing={2}>
              {plans
              .filter(plan => plan.isActive)
              .map((plan, i) => (
                <Grid size={{xs:12, sm:6, md:3}}  key={plan.priceId}>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor:capitalize(plan.name) === capitalize(currentPlan)? "blueviolet" : "grey"}}>
                    <Typography sx={{height:30}} fontWeight={600}>{plan.name} 
                     { capitalize(plan.name) === capitalize(currentPlan) &&
                      <Chip sx={{mx:2}} color="primary" variant="filled" label="Current Plan" />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{py:1, height:60}}>{plan.description}</Typography>

                    <Card variant='outlined' sx={{borderRadius:2, p:1}}>
                    <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" color="text.secondary" sx={{ p: 1 }}>{plan.name === 'Basic' ? 'Trail plan' : 'Billed monthly'}</Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ p: 1 }}>{plan.currency +' : '+ plan.monthlyPrice }</Typography>
                    </Grid>
                    </Card>

                    <Stack flexDirection="column" spacing={2}  sx={{ my: 2 }}>
                      <Card variant='outlined' sx={{borderRadius:2, p:2}}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={500}>Features:</Typography>
                        <Stack spacing={1} sx={{ my: 1 }}>
                          {Object.entries(plan.features).map(([key, enabled]) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon fontSize="small" color={enabled ? "success" : "disabled"} sx={{ mr: 1 }} />
                              <Typography variant="body2" color={enabled ? "text.primary" : "text.secondary"}>
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                      </Card>
                      <Card variant='outlined' sx={{borderRadius:2, p:2}}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={500}>Usage Limits:</Typography>
                        <Stack spacing={1} sx={{ my: 1 }}>
                          {Object.entries(plan.usageLimits).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: {value}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                      </Card>
                    </Stack>

                    <Button
                      variant="contained"
                      fullWidth
                      disabled={plan.name === 'Trial'? true : false}
                      onClick={() => handleSubscribe(plan)}  //pass original price ID here
                    >
                      {plan.name === 'Trial' ? "Trial Plan" : capitalize(plan.name) === capitalize(currentPlan)? "Current Plan" : "Buy"}
                    </Button>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Billing History */}
        <Card elevation={0} sx={{ borderRadius: 2, mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600}>Billing History</Typography>
            <Box sx={{ height: 300 }}>
              <DataGrid
                rows={billingRows}
                columns={billingColumns}
                hideFooterPagination
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{ border: 'none' }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
